import { useCallback, useEffect, useLayoutEffect, useRef, useState, type CSSProperties, type PointerEvent } from 'react';
import { useTranslation } from 'react-i18next';
import type { IMonitorReplay, IReplayEvent } from '@interfaces/IMonitorApi';
import { TrackMarkerRows } from './TrackMarkerRows';
import { REPLAY_LANES, type IDayMarkers } from './markers';
import type { ReplayMarkerView } from './useMarkerView';
import { TrackDay, type IReplayShift } from './TrackDay';
import { dayScale, pointerMs, siteClock, TRACK_INSET_PX } from './trackScale';
import styles from './ReplayTrack.module.css';

export type { IReplayShift };
export interface IReplayTrackProps {
  replay: IMonitorReplay;
  atMs: number;
  tz: string;
  /** Shift start/end lines (below the track); null on a non-working day. Keep it referentially stable. */
  shift: IReplayShift | null;
  /** The day's markers, placed once (see dayMarkers); keep it referentially stable. */
  markers: IDayMarkers;
  markerView: ReplayMarkerView;
  onSeek(ms: number, commit: boolean): void;
  onMarker(event: IReplayEvent): void;
}

/** The track's width until it is measured. */
const DEFAULT_TRACK_PX = 1000;

/**
 * The replay day track: the day layers and markers render once per day (and
 * per width); a playback tick re-renders only the playhead.
 */
export function ReplayTrack({ replay, atMs, tz, shift, markers, markerView, onSeek, onMarker }: IReplayTrackProps) {
  const { t } = useTranslation();
  const trackRef = useRef<HTMLDivElement>(null);
  const dragging = useRef(false);
  const [widthPx, setWidthPx] = useState(DEFAULT_TRACK_PX);
  const { start, end, dataEnd, percentAt } = dayScale(replay);

  useLayoutEffect(() => {
    const observer = new ResizeObserver(([entry]) => {
      const width = (entry as ResizeObserverEntry).contentRect.width;
      // A hidden track measures 0; keep the last width rather than splitting every marker.
      if (width > 0) setWidthPx(width - 2 * TRACK_INSET_PX);
    });
    observer.observe(trackRef.current as HTMLDivElement);
    return () => observer.disconnect();
  }, []);

  // The caller's handler changes every render; the memoized markers get a stable one.
  const markerHandler = useRef(onMarker);
  useEffect(() => {
    markerHandler.current = onMarker;
  });
  const stableOnMarker = useCallback((e: IReplayEvent) => markerHandler.current(e), []);

  const msAt = (event: PointerEvent<HTMLDivElement>) => {
    const bounds = event.currentTarget.getBoundingClientRect();
    // Never before the window, never into the hatched future.
    return pointerMs(event.clientX - bounds.left, bounds.width, start, end, dataEnd);
  };

  /** Ends a scrub however it ended: release, cancel, or capture lost. */
  const endScrub = (track: HTMLDivElement) => {
    dragging.current = false;
    delete track.dataset.scrubbing;
  };

  return (
    <div
      ref={trackRef}
      role="slider"
      aria-label={t('monitor.replay.trackLabel')}
      aria-valuemin={start}
      aria-valuemax={dataEnd}
      aria-valuenow={Math.min(Math.max(atMs, start), dataEnd)}
      aria-valuetext={siteClock(atMs, tz)}
      className={styles.track}
      data-markers={markerView}
      style={{ '--lanes': REPLAY_LANES.length } as CSSProperties}
      onPointerDown={(event) => {
        dragging.current = true;
        event.currentTarget.dataset.scrubbing = 'true';
        event.currentTarget.setPointerCapture(event.pointerId);
        onSeek(msAt(event), false);
      }}
      onPointerMove={(event) => {
        if (dragging.current) onSeek(msAt(event), false);
      }}
      onPointerUp={(event) => {
        if (!dragging.current) return;
        endScrub(event.currentTarget);
        onSeek(msAt(event), true);
      }}
      onPointerCancel={(event) => endScrub(event.currentTarget)}
      onLostPointerCapture={(event) => endScrub(event.currentTarget)}
    >
      {/* The time axis, inset so the knob at 0% / 100% stays inside the track. */}
      <div className={styles.plot} style={{ left: TRACK_INSET_PX, right: TRACK_INSET_PX }} data-testid="replay-plot">
        <TrackDay replay={replay} tz={tz} shift={shift} />
        <TrackMarkerRows replay={replay} markers={markers} view={markerView} tz={tz} widthPx={widthPx} onMarker={stableOnMarker} />
        <div className={styles.head} style={{ left: `${percentAt(atMs)}%` }} data-testid="replay-head">
          {/* The grab handle: pressing it bubbles to the track's scrub handler. */}
          <span className={styles.knob} data-testid="replay-knob" aria-hidden />
        </div>
      </div>
    </div>
  );
}
